import { useFormik } from "formik";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { useContext, useMemo, useRef, useState } from "react";
import useGetEnums from "../../../../Utils/EnumsUtils";
import EnumsContext from "../../../../context/enums-context";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { useLocation, useNavigate } from "react-router-dom";
import useApi from "../../../../Utils/BackendClient";
import { Toast } from "primereact/toast";
import { Column } from "primereact/column";
import { formatCurrency } from "../../../../Utils/Utils";
import { Toolbar } from "primereact/toolbar";

import commonStyle from "../../CommonStyles.module.css";

const NewReport = () => {

    const enumTypes = useMemo(() => ["divisions"], []);

    useGetEnums(enumTypes);

    const ctx = useContext(EnumsContext);
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef();

    const { downloadReport, fetchReportData } = useApi();

    const [downloadButtonLoading, setDownloadButtonLoading] = useState(true);
    const [generationLoading, setGenerationLoading] = useState(false);
    const [lazyState, setLazyState] = useState({
        first: 0,
        rows: 10,
        page: 1
    });
    const [reportData, setReportData] = useState(null);
    const [reportId, setReportId] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);

    const getReportsData = async (values, lazyState, reportId) => {

        setGenerationLoading(true);
        values.reportName = location.state.name;
        const response = await fetchReportData(values, lazyState, reportId);

        if (response.status === 200) {
            setReportData(response.data.results);
            setTotalRecords(response.data.count);
            setReportId(response.data.report_key);
        }
        else {
            toast.current.show({ severity: "error", summary: "Помилка формування звіту", detail: "Помилка сервера " + response.status, life: 3000 });
        }

        setGenerationLoading(false);

        const es = new EventSource(`${process.env.REACT_APP_BACKEND_URL}reports/report-status-stream/?report_key=${response.data.report_key}`);

        es.addEventListener("status", (e) => {
            const data = JSON.parse(e.data);
            console.log("Status update:", data);
        });

        es.addEventListener("complete", (e) => {
            const data = JSON.parse(e.data);
            setDownloadButtonLoading(false);
            console.log("Report completed:", data);
            es.close();
        });


        es.onerror = (err) => {
            console.error("SSE error:", err);
        };

    }

    const formik = useFormik({
        initialValues: {
            period: null,
            division: null,
        },
        enableReinitialize: true,
        onSubmit: async (values) => {
           getReportsData(values, lazyState);
        }
    })

    const onPage = (event) => {
        event.page = event.page + 1;
        getReportsData(formik.values, event, reportId);
        setLazyState(event);
    };

    const toolbarLeftTemplate = (name) => {
        return (<h3>{location.state.presentation}</h3>)
    }

    const toolbarRightTemplate = () => {
        return (<Button label="Вийти" severity="secondary" className={commonStyle.closeButton} type="button" onClick={() => navigate("/reports")} />)
    }

    const CalendarFilter = (
        <FloatLabel>
            <Calendar
                dateFormat="dd/mm/yy"
                value={formik.values.period}
                onChange={(e) => formik.setFieldValue("period", e.value)}
                selectionMode="range"
                readOnlyInput
                hideOnRangeSelection
            />
            <label>Період</label>
        </FloatLabel>
    );

    const DivisionFilter = (
        <FloatLabel>
            <Dropdown
                value={formik.values.division}
                onChange={(e) => formik.setFieldValue("division", e.value)}
                options={ctx.enums.divisions.data}
                style={{ width: '15rem' }}
            />
            <label>Точка продажу</label>
        </FloatLabel>
    );

    const renderCurrencyTable = (arr) => {
        if (!arr || !Array.isArray(arr) || arr.length === 0) return "-";

        return (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                    {arr.map((item, index) => (
                        <tr key={index}>
                            <td style={{ padding: "2px 4px" }}>{formatCurrency(item.sum, item.currency)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const DownloadXlsReport = async () => {

        const response = await downloadReport(reportId);

        console.log(response);
        if (response.status === 200) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            console.log(url);

            const link = document.createElement('a');
            link.href = url;

            link.download = `${reportId}.xlsx`;
            document.body.appendChild(link);
            link.click();

            // Clean up
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.current.show({ severity: 'success', summary: 'Створення документу', detail: 'Документ створено', life: 3000 });
        }
    }

    return (<form onSubmit={formik.handleSubmit}>
        <Toast ref={toast} />
        <Toolbar start={toolbarLeftTemplate} end={toolbarRightTemplate}/>
        <Card className="mb-3">
            <div className="grid">
                <div className="flex flex-row gap-2">
                    {CalendarFilter}
                    {DivisionFilter}
                    <Button label="Cформувати" type="submit" severity="success" loading={generationLoading} outlined/>
                    {reportData && <Button icon="pi pi-download" severity="secondary" loading={downloadButtonLoading} outlined type="button" onClick={DownloadXlsReport}/>}
                </div>
            </div>
        </Card>
        {reportData && <DataTable value={reportData} lazy rows={lazyState.rows} paginator onPage={onPage} totalRecords={totalRecords} rowsPerPageOptions={[10, 20, 50]} first={lazyState.first}>
            <Column field="open_time" header="Час відкриття" />
            <Column field="close_time" header="Час закриття" />
            <Column field="opened_by" header="Відкрита" />
            <Column field="closed_by" header="Закрита" />
            <Column field="status" header="Статус" />
            <Column field="balance_on_open" header="Баланс при відкритті" body={(rowData) => renderCurrencyTable(rowData.balance_on_open)} />
            <Column field="balance_on_close" header="Баланс при закритті" body={(rowData) => renderCurrencyTable(rowData.balance_on_close)} />
            <Column field="cash_deposits" header="Внесення" body={(rowData) => renderCurrencyTable(rowData.cash_deposits)} />
            <Column field="cash_withdraws" header="Зняття" body={(rowData) => renderCurrencyTable(rowData.cash_withdraws)} />
            <Column field="sales" header="Продажі" body={(rowData) => renderCurrencyTable(rowData.sales)} />
            <Column field="returns" header="Повернення" body={(rowData) => renderCurrencyTable(rowData.returns)} />
            <Column field="revenue" header="Виручка" body={(rowData) => renderCurrencyTable(rowData.revenue)} />
        </DataTable>}
    </form>
    );
}

export default NewReport;